/**
 * Browse and join channels — V7-04 §1.
 *
 * This dialog is the whole difference between a channel and a group made
 * visible: a group is somewhere you are put, a channel is somewhere you can
 * find and walk into. Everything else about the two is the same, which is why
 * the server answers both from one `Conversation` with one policy object.
 *
 * Private channels are absent here for the people who are not in them — not
 * greyed out, not "request access". Their existence is part of what is private,
 * so the server never lists them and this component never has to hide them.
 */

import { useState } from 'react';
import { Hash, Loader2, Lock, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useBrowseChannels, useCreateChannel, useJoinChannel } from './api';

export function ChannelBrowser({
  open,
  onOpenChange,
  onOpened,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the conversation id once a channel is joined or created. */
  onOpened: (conversationId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);

  const channels = useBrowseChannels(query, open);
  const join = useJoinChannel();
  const create = useCreateChannel();

  const reset = () => {
    setQuery('');
    setCreating(false);
    setName('');
    setIsPrivate(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg" data-testid="channel-browser">
        <DialogTitle>Channels</DialogTitle>
        <DialogDescription className="sr-only">
          Browse public channels, join one, or create a new channel.
        </DialogDescription>

        {creating ? (
          <form
            className="space-y-3"
            data-testid="create-channel-form"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate(
                { name, visibility: isPrivate ? 'private' : 'public' },
                {
                  onSuccess: (channel) => {
                    reset();
                    onOpenChange(false);
                    onOpened(channel._id);
                  },
                  onError: (error) => toast.error(messageOf(error)),
                },
              );
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="channel-name">Name</Label>
              <div className="flex items-center gap-1.5">
                <Hash className="size-4 vt-muted" />
                <Input
                  id="channel-name"
                  data-testid="channel-name"
                  autoFocus
                  value={name}
                  placeholder="product-design"
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              {/* The server normalises anyway; saying so beats a rejection. */}
              <p className="text-[11px] vt-muted">
                Lower case, no spaces — “Product Design” becomes product-design.
              </p>
            </div>

            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                data-testid="channel-private"
                checked={isPrivate}
                onChange={(event) => setIsPrivate(event.target.checked)}
              />
              Private — invisible to anyone you do not add
            </label>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                Back
              </Button>
              <Button
                type="submit"
                data-testid="create-channel-submit"
                disabled={!name.trim() || create.isPending}
              >
                {create.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Create channel
              </Button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                data-testid="channel-search"
                placeholder="Search channels"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <Button
                variant="secondary"
                data-testid="new-channel"
                onClick={() => setCreating(true)}
              >
                <Plus className="size-3.5" />
                New
              </Button>
            </div>

            <div className="max-h-80 overflow-y-auto" data-testid="channel-list">
              {channels.isLoading && <p className="p-3 text-[12px] vt-muted">Loading…</p>}

              {!channels.isLoading && (channels.data?.length ?? 0) === 0 && (
                <p className="p-3 text-[12px] vt-muted" data-testid="channel-list-empty">
                  {query
                    ? `No channels match “${query}”.`
                    : 'No channels yet. Create the first one.'}
                </p>
              )}

              {channels.data?.map((channel) => (
                <div
                  key={channel._id}
                  data-testid="channel-row"
                  data-channel-name={channel.name}
                  data-member={channel.isMember ? 'true' : 'false'}
                  className="flex items-center gap-2 border-b border-[var(--border)] px-1 py-2 last:border-0"
                >
                  {channel.visibility === 'private' ? (
                    <Lock className="size-3.5 vt-muted" />
                  ) : (
                    <Hash className="size-3.5 vt-muted" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className={cn('truncate text-[13px]', channel.isArchived && 'vt-muted')}>
                      {channel.name}
                      {channel.isArchived && (
                        <span className="ml-1.5 text-[11px] vt-muted">(archived)</span>
                      )}
                    </p>
                    <p className="text-[11px] vt-muted">
                      {channel.memberCount} {channel.memberCount === 1 ? 'member' : 'members'}
                    </p>
                  </div>

                  {channel.isMember ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="channel-open"
                      onClick={() => {
                        onOpenChange(false);
                        onOpened(channel._id);
                      }}
                    >
                      Open
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      data-testid="channel-join"
                      disabled={join.isPending}
                      onClick={() =>
                        join.mutate(channel._id, {
                          onSuccess: () => {
                            onOpenChange(false);
                            onOpened(channel._id);
                          },
                          onError: (error) => toast.error(messageOf(error)),
                        })
                      }
                    >
                      Join
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function messageOf(error: unknown): string {
  const detail = (error as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data?.error?.message;
  return detail ?? 'Something went wrong.';
}
